import { useState, useContext, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useCookies } from 'react-cookie'

import { TodoContentBox } from '../../misc'
import { DocumentsContext, UserContext, ValidationContext } from '../../../context'
import { Modal } from '../../ui_new'
import { Close } from '../../../assets/icons'
import { VALIDATION_TIMESTAMPS_COOKIE_NAME, LAST_OPENED_IMMODOCS_COOKIE_NAME } from '../../../utils'

const ToDoModal = ({ onClose }) => {
  const history = useHistory()
  
  const { myValidations, validationsFetched } = useContext(ValidationContext)
  const { documents } = useContext(DocumentsContext)
  const { user } = useContext(UserContext)

  const [tasksReady, setTasksReady] = useState(false)
  const [newActivity, setNewActivity] = useState([])
  const [tasksToValidate, setTasksToValidate] = useState([])
  const [tasksToCorrect, setTasksToCorrect] = useState([])
  const [tasksValidated, setTasksValidated] = useState([])
  // const [tasksCancelled, setTasksCancelled] = useState([])

  const [cookies, setCookie] = useCookies()
  const [lastOpenedImmodocsTimestamp, setLastOpenedImmodocsTimestamp] = useState(null)

  useEffect(() => {
    const lastOpenedImmodocs = cookies[LAST_OPENED_IMMODOCS_COOKIE_NAME] || 0
    if(lastOpenedImmodocsTimestamp === null) {
      setLastOpenedImmodocsTimestamp(lastOpenedImmodocs)
      setCookie(LAST_OPENED_IMMODOCS_COOKIE_NAME, Date.now(), { path: '/' })
    }
  }, [cookies, setCookie, lastOpenedImmodocsTimestamp])

  useEffect(() => {
    if(!validationsFetched) {
      setTasksReady(false)
      return
    }
    const validationsArray = []
    for(const key in myValidations) {
      validationsArray.push({
        ...myValidations[key],
        id: key
      })
    }
    const tasksToValidateFiltered = validationsArray.filter(validation => validation.status === 'pending')
    const tasksToCorrectFiltered = validationsArray.filter(validation => validation.status === 'refused')
    const tasksValidatedFiltered = validationsArray.filter(validation => validation.status === 'validated')
    // const tasksCancelledFiltered = validationsArray.filter(validation => validation.status === 'cancelled')
    
    setTasksToValidate(tasksToValidateFiltered)
    setTasksToCorrect(tasksToCorrectFiltered)
    setTasksValidated(tasksValidatedFiltered)
    // setTasksCancelled(tasksCancelledFiltered)
    setTasksReady(true)

    const lastValidationTimestampPerValidation = cookies[VALIDATION_TIMESTAMPS_COOKIE_NAME] || {}

    // build events from myvalidations
    const events = []
    for(const key in myValidations) {
      const validation = myValidations[key]
      if(validation.status !== 'pending') continue
      const lastValidationTimestamp = lastValidationTimestampPerValidation[key] || 0
      if(validation.meta?.created && validation.meta.created > lastValidationTimestamp) {
        events.push({
          ...validation,
          comment: `Vient de vous être soumis pour révision ${validation.type === 'partial' ? 'partielle' : 'finale'}`,
          date: validation.meta.created,
        })
      }
      const document = documents[validation.documentId]
      if(!document) {
        continue
      }
      const comments = document.comments || []
      for(const comment of comments) {
        if(comment.date > lastValidationTimestamp) {
          events.push({
            ...validation,
            comment: `Nouveau commentaire de ${comment.author?.firstname} ${comment.author?.lastname}`,
            date: comment.date,
          })
        }
      }
    }
    // sort events by date
    events.sort((a, b) => {
      if(a.date < b.date) {
        return 1
      }
      if(a.date > b.date) {
        return -1
      }
      return 0
    })
    // set new activity
    setNewActivity(events)
    
  }, [myValidations, validationsFetched, cookies, setCookie, documents])

  const handleTitleClick = (route, type, status) => {
    history.push("/to-do")
  }

  if(newActivity.length === 0 || !newActivity.find(activity => activity.date > lastOpenedImmodocsTimestamp)) {
    // don't show modal if no new activity since last opened
    return null
  }

  return (
    <Modal onClose={onClose} className={`modal-v2 ${user?.validation_access === 'negotiator' ? 'modal-v2--large' : 'modal-v2--mid-large-2'}`}>
      <div className='modal-v2__heading'>
        <div>
          <h2>To do list</h2>
        </div>
        <div className="buttons-row">
          <button className="btn btn--with-icon btn--large btn--primary-light" onClick={onClose}><Close /> Fermer</button>
        </div>
      </div>
      <div className='modal-v2__body'>
        <div className="todo-content">
          <p>Accéder directement aux nouvelles activités des documents qui vous sont affectés, afin de les réaliser dans les temps.</p>
          <div className={`todo-content__boxes-wrapper`}>
            <div className="todo-content__boxes">
              <div className='column-parent'>
                <div className={`container-element container-element--columns-${user?.validation_access === 'negotiator' ? 4 : 3}`}>

                  {/* New activity */}
                  <div className={`column`}>

                    <TodoContentBox
                      onTitleClick={handleTitleClick}
                      title="Nouvelles activités"
                      emptyLabel={'Aucune nouvelle activité'}
                      actions={newActivity}
                      loading={!tasksReady}
                      type="event"
                      sorted={true}
                    />
                  </div>
                  {/* To validate tasks */}
                  <div className={`column`}>

                    <TodoContentBox
                      onTitleClick={handleTitleClick}
                      title={user?.validation_access === 'manager' ? "À valider" : "En revision"}
                      emptyLabel={'Aucune action de validation en cours'}
                      actions={tasksToValidate}
                      loading={!tasksReady}
                      color="yellow"
                    />
                  </div>

                  {/* To correct tasks */}
                  <div className={`column`}>
                    <TodoContentBox
                      onTitleClick={handleTitleClick}
                      title="À corriger"
                      emptyLabel={'Aucune action de validation à corriger'}
                      actions={tasksToCorrect}
                      loading={!tasksReady}
                      color="red"
                    />
                  </div>

                  {/* Validated tasks */}
                  {user?.validation_access === 'negotiator' &&
                    <div className={`column`}>
                      <TodoContentBox
                        title="Validé"
                        emptyLabel={'Aucune action de validation validée'}
                        actions={tasksValidated}
                        loading={!tasksReady}
                        color="green"
                      />
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default ToDoModal
